import React, { useEffect, useState } from "react";
import AgoraRTC, { IAgoraRTCClient, ILocalAudioTrack, IAgoraRTCRemoteUser } from "agora-rtc-sdk-ng";
import { Box, Typography, IconButton, Paper, Button, CircularProgress } from "@mui/material";
import { Mic, MicOff, CallEnd, Person } from "@mui/icons-material";

interface AgoraCallProps {
  appId: string;
  channelName: string;
  token: string;
  uid: number;
  onClose: () => void;
  onEndCall?: (duration: string) => void;
}

const AgoraCall: React.FC<AgoraCallProps> = ({ appId, channelName, token, uid, onClose, onEndCall }) => {
  const [client, setClient] = useState<IAgoraRTCClient | null>(null);
  const [localAudioTrack, setLocalAudioTrack] = useState<ILocalAudioTrack | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [remoteUsers, setRemoteUsers] = useState<IAgoraRTCRemoteUser[]>([]);
  const [status, setStatus] = useState<"joining" | "connected" | "error">("joining");
  const [errorMessage, setErrorMessage] = useState("");
  const [startTime] = useState(Date.now());

  useEffect(() => {
    let agoraClient: IAgoraRTCClient | null = null;
    let audioTrack: ILocalAudioTrack | null = null;

    const init = async () => {
      try {
        agoraClient = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
        setClient(agoraClient);

        agoraClient.on("user-published", async (user, mediaType) => {
          if (agoraClient) {
            await agoraClient.subscribe(user, mediaType);
            if (mediaType === "audio") {
              user.audioTrack?.play();
            }
            setRemoteUsers((prev) => {
              if (prev.find(u => u.uid === user.uid)) return prev;
              return [...prev, user];
            });
          }
        });

        agoraClient.on("user-unpublished", (user) => {
          setRemoteUsers((prev) => prev.filter((u) => u.uid !== user.uid));
        });

        agoraClient.on("user-left", (user) => {
          setRemoteUsers((prev) => prev.filter((u) => u.uid !== user.uid));
        });

        setStatus("joining");
        console.log("Agora Initialization Info:");
        console.log("  - AppID:", appId);
        console.log("  - Channel:", channelName);
        console.log("  - Token:", token ? (token.substring(0, 10) + "...") : "NULL");
        console.log("  - UID:", uid);
        
        if (!appId || appId === "API_KEY") {
           alert("CRITICAL ERROR: AppID is missing or invalid! Value: " + appId);
        }
        
        await agoraClient.join(appId, channelName, token || null, uid || 0);
        
        audioTrack = await AgoraRTC.createMicrophoneAudioTrack();
        setLocalAudioTrack(audioTrack);
        await agoraClient.publish([audioTrack]);
        setStatus("connected");
      } catch (error: any) {
        console.error("Agora join failed", error);
        setStatus("error");
        setErrorMessage(error.message || "Failed to join call. Check microphone permissions or Agora token.");
      }
    };

    init();

    return () => {
      if (audioTrack) {
        audioTrack.stop();
        audioTrack.close();
      }
      if (agoraClient) {
        agoraClient.leave().catch(console.error);
      }
    };
  }, [appId, channelName, token, uid]);

  const toggleMute = () => {
    if (localAudioTrack) {
      localAudioTrack.setMuted(!isMuted);
      setIsMuted(!isMuted);
    }
  };

  const handleEndCall = async () => {
    const durationMs = Date.now() - startTime;
    const mins = Math.floor(durationMs / 60000);
    const secs = Math.floor((durationMs % 60000) / 1000);
    const durationStr = `${mins}:${secs < 10 ? '0' : ''}${secs}`;

    if (onEndCall) {
        onEndCall(durationStr);
    }

    if (localAudioTrack) {
      localAudioTrack.stop();
      localAudioTrack.close();
    }
    if (client) {
      await client.leave();
    }
    onClose();
  };

  return (
    <Paper
      elevation={3}
      sx={{
        position: "fixed",
        bottom: 20,
        right: 20,
        width: 300,
        p: 2,
        borderRadius: 4,
        zIndex: 10000,
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(10px)",
        textAlign: "center",
      }}
    >
      <Typography variant="h6" gutterBottom>
        Conference Call
      </Typography>

      {status === "error" ? (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography color="error" variant="body2" mb={2}>{errorMessage}</Typography>
          <Button variant="outlined" size="small" onClick={onClose}>Close</Button>
        </Box>
      ) : (
        <Box sx={{ position: "relative" }}>
          {status === "joining" && (
            <Box sx={{ 
              position: "absolute", top: 0, left: 0, right: 0, bottom: 0, 
              bgcolor: "rgba(255,255,255,0.7)", zIndex: 2,
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              borderRadius: 2
            }}>
              <CircularProgress size={30} sx={{ mb: 1 }} />
              <Typography variant="caption">Connecting...</Typography>
            </Box>
          )}
          
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                backgroundColor: "primary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                boxShadow: 3,
              }}
            >
              <Person fontSize="large" />
            </Box>
          </Box>

          <Typography variant="body2" color="text.primary" mb={2}>
            {remoteUsers.length > 0
              ? `${remoteUsers.length} Participant(s) Joined`
              : "Waiting for others to join..."}
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
            <IconButton
              onClick={toggleMute}
              sx={{
                backgroundColor: isMuted ? "error.main" : "grey.200",
                color: isMuted ? "white" : "inherit",
                "&:hover": { backgroundColor: isMuted ? "error.dark" : "grey.300" },
              }}
            >
              {isMuted ? <MicOff /> : <Mic />}
            </IconButton>
            <IconButton
              onClick={handleEndCall}
              sx={{
                backgroundColor: "error.main",
                color: "white",
                "&:hover": { backgroundColor: "error.dark" },
              }}
            >
              <CallEnd />
            </IconButton>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default AgoraCall;
